import React from 'react';
import { Task, TaskStatus } from '../types';
import { fmtTimestamp } from '../utils/dateFormat';

interface Props {
  task: Task;
  onClick: () => void;
  isSelected?: boolean;
}

const TaskCard: React.FC<Props> = ({ task, onClick, isSelected }) => {
  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.QUEUED:
        return '#f59e0b';
      case TaskStatus.IN_PROGRESS:
        return '#3b82f6';
      case TaskStatus.READY_FOR_REVIEW:
        return '#8b5cf6';
      case TaskStatus.COMPLETED:
        return '#10b981';
      case TaskStatus.FAILED:
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.QUEUED:
        return '⏳';
      case TaskStatus.IN_PROGRESS:
        return '⚡';
      case TaskStatus.READY_FOR_REVIEW:
        return '👀';
      case TaskStatus.COMPLETED:
        return '✓';
      case TaskStatus.FAILED:
        return '✗';
      default:
        return '○';
    }
  };

  const getStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.IN_PROGRESS:
        return 'WORKING';
      case TaskStatus.READY_FOR_REVIEW:
        return 'NEEDS REVIEW';
      default:
        return status.replace('_', ' ').toUpperCase();
    }
  };

  const isActive = task.status === TaskStatus.IN_PROGRESS;
  const needsReview = task.status === TaskStatus.READY_FOR_REVIEW;

  // For a finished or review-ready task, show WHEN it reached that state beside the card. Completed
  // and failed runs stamp completed_at; a review-ready task uses its last update. Falls back to
  // updated_at so a date always shows for these states.
  const getStatusDate = (): { label: string; iso?: string | null } | null => {
    switch (task.status) {
      case TaskStatus.COMPLETED:
        return { label: 'Completed', iso: task.completed_at || task.updated_at };
      case TaskStatus.FAILED:
        return { label: 'Failed', iso: task.completed_at || task.updated_at };
      case TaskStatus.READY_FOR_REVIEW:
        return { label: 'In review since', iso: task.updated_at };
      default:
        return null;
    }
  };
  const statusDate = getStatusDate();
  const statusDateText = statusDate ? fmtTimestamp(statusDate.iso) : '';

  return (
    <>
      <style>
        {`
          @keyframes taskCardPulse {
            0%, 100% {
              opacity: 1;
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
            }
            50% {
              opacity: 0.9;
              box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
            }
          }
          @keyframes statusPulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }
        `}
      </style>
      <div
        onClick={onClick}
        style={{
          border: isSelected ? '2px solid #3b82f6' : isActive ? '2px solid #3b82f6' : needsReview ? '2px solid #8b5cf6' : '1px solid #ddd',
          borderRadius: '8px',
          padding: '16px',
          margin: '8px 0',
          cursor: 'pointer',
          transition: 'all 0.2s',
          backgroundColor: isSelected ? '#eff6ff' : '#fff',
          animation: isActive ? 'taskCardPulse 2s ease-in-out infinite' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#333', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{task.title}</h4>
            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {task.description.length > 100
                ? task.description.substring(0, 100) + '...'
                : task.description}
            </p>
            <div style={{ fontSize: '12px', color: '#999' }}>
              <span>Created by {task.created_by}</span>
              <span style={{ margin: '0 8px' }}>•</span>
              <span>{new Date(task.created_at).toLocaleString()}</span>
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '5px',
            flexShrink: 0,
            minWidth: '140px',
            textAlign: 'right',
          }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 12px',
                borderRadius: '12px',
                backgroundColor: getStatusColor(task.status) + '20',
                color: getStatusColor(task.status),
                fontSize: '13px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{
                animation: isActive ? 'statusPulse 1s ease-in-out infinite' : 'none',
              }}>{getStatusIcon(task.status)}</span>
              <span>{getStatusLabel(task.status)}</span>
            </div>
            {isActive && (
              <div style={{
                fontSize: '11px',
                color: '#3b82f6',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <span style={{
                  display: 'inline-block',
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  animation: 'statusPulse 1s ease-in-out infinite',
                }}></span>
                AI is working...
              </div>
            )}
            {needsReview && (
              <div style={{
                fontSize: '11px',
                color: '#8b5cf6',
                fontWeight: '500',
              }}>
                Click to review
              </div>
            )}
            {statusDate && statusDateText && (
              <div style={{
                fontSize: '11px',
                color: getStatusColor(task.status),
                fontWeight: '500',
                textAlign: 'right',
                lineHeight: 1.35,
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}>
                {statusDate.label} {statusDateText}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskCard;
